import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import DaySelector from '../components/DaySelector.jsx';
import EventListing from '../components/EventListing.jsx';
import Hero from '../components/Hero.jsx';

import Spinner from '../../../react-shared/Spinner.jsx';

import { useEventsQuery } from '../redux/api.slice.js';
import { updateLocationQuery } from '../../../utils/updateLocationQuery.js';
import ShareWidget from '../../../react-shared/ShareWidget.jsx';

const dateFormat = 'yyyy-MM-dd';
const dateTimeFormat = 'yyyy-MM-dd H:mm';
const trackTimezone = 'Australia/Melbourne';

const getEventStartDateTimeString = (event) => `${event.date} ${event.startTime}`;
const getEventEndDateTimeString = (event) => `${event.date} ${event.endTime}`;

function App({
  ads = [], adBasement = '', currentTimeOverride = '', descriptionSaved = '', descriptionSchedule = '', descriptionShared = '', logoAlt, logoText = '', logoUrl, shareWidgetKeys, sideImage = '',
}) {
  const params = Object.fromEntries(new URLSearchParams(window.location.search));

  const savedEvents = useSelector((state) => state.savedEvents);

  const [activeCategories, setActiveCategories] = useState(params.categories ? params.categories.split(',') : []);
  const [currentDay, setCurrentDay] = useState(params.day || '');
  const [currentTimezone, setCurrentTimezone] = useState(trackTimezone);
  const [eventsByDay, setEventsByDay] = useState([]);
  const [useLocalTime, setUseLocalTime] = useState(params.useLocalTime || false);

  const todayDate = currentTimeOverride
    ? currentTimeOverride.split(' ')[0]
    : DateTime.local().setZone(currentTimezone).toFormat(dateFormat);

  const getDateTimeObject = (date, format) => DateTime
    .fromFormat(date, format, { zone: trackTimezone })
    .setZone(currentTimezone);

  const convertQueryDataToEventsByDay = (queryData) => {
    /**
    * @typedef {Object} EventGroup
    * @property {string} date - Events' date.
    * @property {any[]} events - An array of events.
    */
    /** @type {EventGroup[]} */
    const eventsGroupedByDays = [];

    queryData.data.forEach((event) => {
      const eventDateTime = DateTime.fromFormat(
        getEventStartDateTimeString(event),
        dateTimeFormat,
        { zone: trackTimezone },
      ).setZone(currentTimezone);

      const eventDate = eventDateTime.toFormat(dateFormat);
      const currentEventGroup = eventsGroupedByDays.find((day) => day.date === eventDateTime.toFormat(dateFormat));

      // Add a new day if doesn't exist with current event or just add this event to it's day's object
      if (currentEventGroup) {
        currentEventGroup.events.push(event);
      } else {
        eventsGroupedByDays.push({ date: eventDate, events: [event] });
      }
    });

    // Sort days by date
    eventsGroupedByDays.sort((a, b) => (
      getDateTimeObject(a.date, dateFormat)
      - getDateTimeObject(b.date, dateFormat)
    ));

    // Sort events inside each day by date and time
    eventsGroupedByDays.forEach((day) => day.events.sort((a, b) => {
      const getComparableStartDateTime = (evt) => getDateTimeObject(getEventStartDateTimeString(evt), dateTimeFormat);
      const getComparableEndDateTime = (evt) => getDateTimeObject(getEventEndDateTimeString(evt), dateTimeFormat);

      const compareStartTimes = getComparableStartDateTime(a) - getComparableStartDateTime(b);

      return compareStartTimes === 0
        ? getComparableEndDateTime(a) - getComparableEndDateTime(b)
        : compareStartTimes;
    }));

    const todayIsOneOfTheDates = eventsGroupedByDays.some((day) => [todayDate, currentDay].includes(day.date));

    const currentDayIsValid = currentDay
      ? ['saved', 'shared'].concat(eventsGroupedByDays.map((day) => day.date)).includes(currentDay)
      : false;

    if (!currentDayIsValid) {
      if (todayIsOneOfTheDates) {
        setCurrentDay(todayDate);
      } else {
        setCurrentDay(eventsGroupedByDays[0].date);
      }
    }

    setEventsByDay(eventsGroupedByDays);
  };

  const {
    data, error, isFetching, isSuccess,
  } = useEventsQuery(currentDay === 'shared' ? { filter_event_id: params.eventIds.split(',') } : '');

  // Manages time offset on time swtich
  useEffect(() => {
    if (useLocalTime) {
      setCurrentTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    } else {
      setCurrentTimezone(trackTimezone);
    }
  }, [useLocalTime]);

  // Constructs the sorted events object on API data fetch
  useEffect(() => {
    if (isSuccess) convertQueryDataToEventsByDay(data);
  }, [data, currentTimezone, isSuccess]);

  // Manages URL search params
  useEffect(() => {
    updateLocationQuery({
      categories: activeCategories,
      day: currentDay,
      ...(currentDay === 'shared' && { eventIds: params.eventIds }),
    });
  }, [activeCategories, currentDay]);

  if (error) return <div>{error.message || error.error}</div>;

  if (isFetching || eventsByDay.length === 0) return <Spinner withPadding />;

  if (data && isSuccess && eventsByDay.length > 0) {
    const heroHeading = currentDay === 'shared' ? 'Shared Events' : currentDay === 'saved' ? 'Saved Events' : 'Schedule';
    const heroDescription = currentDay === 'shared' ? descriptionShared : currentDay === 'saved' ? descriptionSaved : descriptionSchedule;

    const categories = Object
      .entries(data.meta.categories)
      .map(([id, { status, title }]) => {
        if (status) {
          return { id, label: title };
        }

        return null;
      })
      .filter((category) => category);

    return (
      <>
        <DaySelector
          currentDay={currentDay}
          currentTimezone={currentTimezone}
          days={eventsByDay.map((day) => ({ date: day.date, events: day.events.length > 0 }))}
          logoAlt={logoAlt}
          logoText={logoText}
          logoUrl={logoUrl}
          setCurrentDay={setCurrentDay}
          sideImage={sideImage}
          todayDate={todayDate}
        />

        <Hero
          activeCategories={activeCategories}
          categories={categories}
          description={heroDescription}
          heading={heroHeading}
          setActiveCategories={setActiveCategories}
          setUseLocalTime={setUseLocalTime}
          useLocalTime={useLocalTime}
        />

        <EventListing
          activeCategories={activeCategories}
          adBasement={adBasement}
          ads={ads}
          currentDay={currentDay}
          currentTimezone={currentTimezone}
          currentTimeOverride={currentTimeOverride}
          eventsByDay={eventsByDay}
        />

        {currentDay !== 'saved' && shareWidgetKeys.length > 0 && (
          <ShareWidget
            shareWidgetKeys={shareWidgetKeys}
            text="Check out what's on at the Grand Prix"
            url={window.location.href}
          />
        )}

        {currentDay === 'saved' && savedEvents.length > 0 && shareWidgetKeys.length > 0 && (
          <ShareWidget
            shareWidgetKeys={shareWidgetKeys}
            text="Check out what's on at the Grand Prix"
            url={`${window.location.href.split('?')[0]}?day=shared&eventIds=${savedEvents.join(',')}`}
          />
        )}
      </>
    );
  }

  return 'Sorry, something went wrong';
}

App.propTypes = {
  adBasement: PropTypes.string,
  ads: PropTypes.arrayOf(PropTypes.string),
  currentTimeOverride: PropTypes.string,
  descriptionSaved: PropTypes.string,
  descriptionSchedule: PropTypes.string,
  descriptionShared: PropTypes.string,
  logoAlt: PropTypes.string.isRequired,
  logoText: PropTypes.string,
  logoUrl: PropTypes.string.isRequired,
  shareWidgetKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  sideImage: PropTypes.string,
};

export default App;
